<template>
  <div
    class="form-page"
    v-loading="busy"
    element-loading-text="处理中…"
    :element-loading-lock="busy"
    :class="{ 'form-page--locked': busy }"
  >
    <!-- 页头：标题 / 返回 + 白底 -->
    <div v-if="showHeader || $slots.header" class="form-page__header">
      <slot name="header">
        <div class="form-page__header-left">
          <el-button type="text" icon="el-icon-arrow-left" @click="emitCancel">返回</el-button>
          <span class="form-page__title">{{ title }}</span>
        </div>
      </slot>
    </div>

    <!-- 内容区域：可编辑，独立滚动，不随底部操作栏滚动 -->
    <div class="form-page__body">
      <slot />
    </div>

    <!-- 底部固定操作栏：取消 / 存草稿 / 提交 -->
    <div class="form-page__footer">
      <slot name="footer-extra" />
      <div class="form-page__actions">
        <el-button :disabled="busy" @click="emitCancel">{{ cancelText }}</el-button>
        <el-button v-if="showDraft" :loading="drafting" :disabled="busy" @click="emitDraft">{{
          draftText
        }}</el-button>
        <el-button
          v-if="showSubmit"
          type="primary"
          class="form-page__submit"
          :class="{ 'is-soft-disabled': submitSoftBlocked }"
          :loading="submitting"
          :disabled="nativeSubmitDisabled"
          :aria-disabled="submitDisabled || busy"
          @click="emitSubmit"
          >{{ submitText }}</el-button
        >
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';

export default Vue.extend({
  name: 'FormPage',
  props: {
    title: { type: String, default: '表单' },
    showHeader: { type: Boolean, default: true },
    cancelText: { type: String, default: '取消' },
    draftText: { type: String, default: '存草稿' },
    submitText: { type: String, default: '提交' },
    showDraft: { type: Boolean, default: true },
    showSubmit: { type: Boolean, default: true },
    submitDisabled: { type: Boolean, default: false },
    submitDisabledClickable: { type: Boolean, default: false },
    submitting: { type: Boolean, default: false },
    drafting: { type: Boolean, default: false },
  },
  computed: {
    busy(): boolean {
      return this.submitting || this.drafting;
    },
    submitSoftBlocked(): boolean {
      return this.submitDisabled && this.submitDisabledClickable && !this.busy;
    },
    nativeSubmitDisabled(): boolean {
      return this.busy || (this.submitDisabled && !this.submitDisabledClickable);
    },
  },
  methods: {
    emitCancel() {
      this.$emit('cancel');
    },
    emitDraft() {
      if (!this.busy) this.$emit('draft');
    },
    emitSubmit() {
      if (!this.busy) this.$emit('submit');
    },
  },
});
</script>

<style lang="scss" scoped>
// 页面级表单容器：占满可视区（.view），主背景白色
.form-page {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: $color-surface;

  // loading 挂在整页容器上，避免 body 滚动后遮罩只盖住可视窗口、底部露白
  ::v-deep .el-loading-mask {
    position: absolute;
    inset: 0;
    z-index: 2000;
  }
}

.form-page--locked .form-page__body {
  overflow: hidden;
  pointer-events: none;
  user-select: none;
}

// 页头：不滚动（flex:none）
.form-page__header {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 20px;
  border-bottom: 1px solid $color-hairline;
  background: $color-surface;

  .form-page__header-left {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .form-page__title {
    font-size: 16px;
    font-weight: 600;
    color: $color-text;
  }
}

// 内容区域：可编辑、独立滚动，滚动仅发生在此区域，底部操作栏固定不跟随
.form-page__body {
  flex: 1;
  min-width: 0;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  padding: 20px 24px;
  background: $color-surface;
  color: $color-text;
}

// 底部固定操作栏：随页面固定于可视区底部，不参与内容滚动
.form-page__footer {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 24px;
  border-top: 1px solid $color-hairline;
  background: $color-surface;

  .form-page__actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-left: auto;
  }

  .form-page__submit.is-soft-disabled {
    opacity: 0.55;
    cursor: pointer;
  }
}

// 移动端：收窄边距、操作按钮加宽（≤760px / html.is-mobile）
@media (max-width: $breakpoint-mobile) {
  .form-page__body {
    padding: 14px 12px;
  }

  .form-page__footer {
    flex-wrap: wrap;
    gap: 10px;
    padding: 12px;
    // 底部安全区（iPhone 横条）：操作栏不被遮挡
    padding-bottom: calc(12px + env(safe-area-inset-bottom));
  }

  .form-page__actions {
    flex: 1 1 100%;
    width: 100%;
    margin-left: 0;

    .el-button {
      flex: 1;
      min-width: 0;
      height: 44px;
    }

    .el-button + .el-button {
      margin-left: 0;
    }
  }
}
</style>
